import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash } from '@phosphor-icons/react';
import {
  EditorForm,
  Field,
  inputClasses,
  textareaClasses,
  type EditorSectionProps,
} from './shared';
import { educationSchema, type EducationValues } from '../schemas';

export const EDUCATION_SEED: EducationValues = {
  items: [],
};

export function EducationEditor({
  initial,
  onDirty,
  onSubmit,
  submitting,
  onDraftChange,
}: EditorSectionProps<EducationValues>) {
  const form = useForm<EducationValues>({
    resolver: zodResolver(educationSchema),
    defaultValues: initial ?? EDUCATION_SEED,
    mode: 'onBlur',
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });

  useEffect(() => {
    onDraftChange?.(form.getValues());
    const sub = form.watch((v) => {
      onDirty();
      onDraftChange?.(v);
    });
    return () => sub.unsubscribe();
  }, [form, onDirty, onDraftChange]);

  const { errors } = form.formState;

  return (
    <EditorForm onSubmit={form.handleSubmit(async (v) => onSubmit(v))} submitting={submitting}>
      <div className="space-y-4">
        {fields.map((f, idx) => {
          const rowErrors = errors.items?.[idx];
          return (
            <div key={f.id} className="space-y-4 rounded-xl border border-line bg-board p-5">
              <Field label="Institution" error={rowErrors?.institution?.message}>
                <input
                  {...form.register(`items.${idx}.institution`)}
                  placeholder="School or program"
                  className={inputClasses}
                />
              </Field>
              <Field label="Credential" error={rowErrors?.credential?.message}>
                <input
                  {...form.register(`items.${idx}.credential`)}
                  placeholder="BFA, Communication Design"
                  className={inputClasses}
                />
              </Field>
              <div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
                <Field label="Start year" error={rowErrors?.startYear?.message}>
                  <input
                    {...form.register(`items.${idx}.startYear`)}
                    placeholder="YYYY"
                    className={inputClasses}
                  />
                </Field>
                <Field label="End year" error={rowErrors?.endYear?.message}>
                  <input
                    {...form.register(`items.${idx}.endYear`)}
                    placeholder="YYYY"
                    className={inputClasses}
                  />
                </Field>
                <Field label="GPA" hint="Optional. e.g. 3.8" error={rowErrors?.gpa?.message}>
                  <input
                    {...form.register(`items.${idx}.gpa`)}
                    placeholder="3.8"
                    className={inputClasses}
                  />
                </Field>
              </div>
              <Field
                label="Notes"
                hint="Honors, thesis, activities. Optional."
                error={rowErrors?.note?.message}
              >
                <textarea
                  {...form.register(`items.${idx}.note`)}
                  rows={3}
                  placeholder="Valedictorian. Thesis on..."
                  className={textareaClasses}
                />
              </Field>
              <div className="flex justify-end border-t border-line pt-3">
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink-muted hover:border-red-300 hover:text-red-700"
                >
                  <Trash size={12} /> Remove
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() =>
          append({
            id: Math.random().toString(36).slice(2, 8),
            institution: '',
            credential: '',
            startYear: '',
            endYear: '',
            gpa: '',
            note: '',
          })
        }
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-board py-4 text-sm font-medium text-ink-muted hover:border-ink hover:text-ink"
      >
        <Plus size={14} /> Add credential
      </button>
    </EditorForm>
  );
}
